/**
 * Validation utilities for conversational onboarding
 */

export const validateEmail = (email: string): { valid: boolean; error?: string } => {
  if (!email || !email.trim()) {
    return { valid: false, error: 'Email is required' };
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return { valid: false, error: 'Please enter a valid email address (e.g., john@example.com)' };
  }

  return { valid: true };
};

export const validatePhone = (phone: string): { valid: boolean; error?: string } => {
  if (!phone || !phone.trim()) {
    return { valid: false, error: 'Phone number is required' };
  }

  // Remove all non-numeric characters
  const cleaned = phone.replace(/\D/g, '');

  // Check if it's 10 digits (US phone number)
  if (cleaned.length !== 10) {
    return { valid: false, error: 'Please enter a valid 10-digit phone number (e.g., 555-123-4567)' };
  }

  return { valid: true };
};

export const validateName = (name: string, fieldName: string): { valid: boolean; error?: string } => {
  if (!name || !name.trim()) {
    return { valid: false, error: `${fieldName} is required` };
  }

  if (name.trim().length < 2) {
    return { valid: false, error: `${fieldName} must be at least 2 characters` };
  }

  return { valid: true };
};

export const validateOTP = (otp: string): { valid: boolean; error?: string } => {
  if (!otp || !otp.trim()) {
    return { valid: false, error: 'OTP is required' };
  }

  // Remove all non-numeric characters
  const cleaned = otp.replace(/\D/g, '');

  // Check if it's 4 digits
  if (cleaned.length !== 4) {
    return { valid: false, error: 'Please enter the 4-digit code sent to your phone' };
  }

  return { valid: true };
};

export const formatPhoneNumber = (phone: string): string => {
  // Remove all non-numeric characters
  const cleaned = phone.replace(/\D/g, '');

  // Format as XXX-XXX-XXXX
  if (cleaned.length === 10) {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }

  return phone;
};

// Return 'true' if the passed value is null or empty
export function IsNullOrEmpty(pVal: any): boolean {
    return (
        typeof pVal === 'undefined' ||
        pVal === null ||
        (typeof pVal === 'string' && String(pVal.trim()).length === 0) ||
        (typeof pVal === 'object' && Object.keys(pVal).length === 0)
    );
}

// Return 'true' if the passed value is not null or empty
export function IsNotNullOrEmpty(pVal: any): boolean {
    return !IsNullOrEmpty(pVal);
}

export function isAuthTokenExpired(token: string | null): boolean {
  if (!token) return true;

  try {
    const payloadBase64 = token.split(".")[1];
    if (!payloadBase64) return true;

    const decodedPayload = JSON.parse(
      atob(payloadBase64.replace(/-/g, "+").replace(/_/g, "/"))
    );

    const exp = decodedPayload.exp;
    if (!exp) return true;

    // exp is in seconds, Date.now() is in milliseconds
    return Date.now() >= exp * 1000;
  } catch (error) {
    // Invalid token format or decoding error
    return true;
  }
};

export const isValidArray = (arr: any) => {
  return arr && Array.isArray(arr) && arr.length > 0;
};